import { z } from 'zod'

/**
 * Who may do what, as pure functions over plain data.
 *
 * The rules live here, next to the reducer, for the same reason the reducer does: the
 * API enforces them and the browser uses them to decide what to show, and the two must
 * not drift. The browser's copy is a convenience — every answer is decided again on the
 * server, which is the only side that can be trusted.
 */

/**
 * SYSTEM_ADMIN and SYSTEM_SUPPORT are a Teamcentric Accounts super admin and support user:
 * they belong to no tenant, and they can sign in and nothing else (tenants and feedback are
 * administered at Accounts).
 */
export const USER_ROLES = ['SYSTEM_ADMIN', 'SYSTEM_SUPPORT', 'TENANT_ADMIN', 'NORMAL_USER'] as const
export type UserRole = (typeof USER_ROLES)[number]

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  SYSTEM_ADMIN: 'System admin',
  SYSTEM_SUPPORT: 'Support',
  TENANT_ADMIN: 'Tenant admin',
  NORMAL_USER: 'User',
}

/** What a share grants. The owner is not a share: ownership is stored on the board. */
export const SHARE_ROLES = ['editor', 'viewer'] as const
export type ShareRole = (typeof SHARE_ROLES)[number]

export const SHARE_ROLE_LABELS: Record<ShareRole, string> = {
  editor: 'Can edit',
  viewer: 'Can view',
}

/** How a user reaches one board. `owner` is the only level that may delete it. */
export type BoardAccess = 'owner' | 'editor' | 'viewer'

export const tenantSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(200),
  /**
   * Off (the default) means POST /mcp refuses every token in the tenant; a tenant admin
   * turns it on. Tokens and the REST API are untouched.
   */
  mcpEnabled: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})

/**
 * Emails identify a person across the whole system, so they are unique globally and a
 * given address belongs to exactly one tenant. Stored and compared in lower case.
 */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(320)
  .refine((value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), 'That is not an email address.')

export const userSchema = z.object({
  id: z.uuid(),
  /** Null only for SYSTEM_ADMIN and SYSTEM_SUPPORT, who belong to no tenant and own no boards. */
  tenantId: z.uuid().nullable(),
  email: emailSchema,
  displayName: z.string().trim().max(120),
  role: z.enum(USER_ROLES),
  disabled: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** Their ID at Teamcentric Accounts once they sign in through it: their name and photo then come from there. */
  accountId: z.string().nullable().optional(),
  /** The public address of their profile photo at Teamcentric Accounts, shown as it is. */
  pictureUrl: z.string().nullable().optional(),
  /** Until when their invitation through Teamcentric Accounts can be accepted; null once accepted, or never invited. */
  invitationExpiresAt: z.iso.datetime().nullable().optional(),
  /** Platform staff from Teamcentric Accounts: their admin ID there. Their name and photo come from Accounts. */
  accountsAdminId: z.string().nullable().optional(),
})

export type Tenant = z.infer<typeof tenantSchema>
export type User = z.infer<typeof userSchema>

/** The signed-in user, as every request and every component sees them. */
export type Actor = Pick<User, 'id' | 'tenantId' | 'role' | 'email' | 'displayName' | 'accountId' | 'pictureUrl' | 'accountsAdminId'>

export interface BoardOwnership {
  tenantId: string
  ownerId: string
}

/** A share row: which user, and what it grants them. */
export interface BoardShare {
  boardId: string
  userId: string
  role: ShareRole
}

export function isSystemAdmin(actor: Pick<Actor, 'role'>): boolean {
  return actor.role === 'SYSTEM_ADMIN'
}

export function isTenantAdmin(actor: Pick<Actor, 'role'>): boolean {
  return actor.role === 'TENANT_ADMIN'
}

/**
 * A tenant admin manages people, not content: their reach over boards is exactly a
 * normal user's. Being an admin never grants sight of a colleague's board.
 */
export function boardAccess(
  actor: Pick<Actor, 'id' | 'tenantId' | 'role'>,
  board: BoardOwnership,
  shares: readonly Pick<BoardShare, 'userId' | 'role'>[],
): BoardAccess | null {
  // A system admin belongs to no tenant and therefore reaches no board. Managing tenants
  // is not a way to read their work. Nor does support.
  if (actor.role === 'SYSTEM_ADMIN' || actor.role === 'SYSTEM_SUPPORT') return null
  if (!actor.tenantId || actor.tenantId !== board.tenantId) return null
  if (actor.id === board.ownerId) return 'owner'
  const share = shares.find((entry) => entry.userId === actor.id)
  return share ? share.role : null
}

/** A project is reached the same way as a board: as its owner, or as a member with a role. */
export const projectAccess = boardAccess

const ACCESS_RANK: Record<BoardAccess, number> = { viewer: 1, editor: 2, owner: 3 }

/**
 * The higher of two ways into a board: directly (owner or share) or through its project.
 * The project's owner reaches every item in it as an owner.
 */
export function strongerAccess(a: BoardAccess | null, b: BoardAccess | null): BoardAccess | null {
  if (!a) return b
  if (!b) return a
  return ACCESS_RANK[a] >= ACCESS_RANK[b] ? a : b
}

export function canEditBoard(access: BoardAccess | null): boolean {
  return access === 'owner' || access === 'editor'
}

/** The owner may delete a board, and so may anyone who can edit the project it is in. */
export function canDeleteBoard(access: BoardAccess | null, projectRole: BoardAccess | null = null): boolean {
  return access === 'owner' || canEditBoard(projectRole)
}

/** Only a project's owner renames it, deletes it, and decides who its members are. */
export function canManageProject(role: BoardAccess | null): boolean {
  return role === 'owner'
}

/** Only the owner decides who else sees a board. */
export function canShareBoard(access: BoardAccess | null): boolean {
  return access === 'owner'
}

/** Managing users is the tenant admin's job, and only inside their own tenant. */
export function canManageUsers(actor: Pick<Actor, 'role'>): boolean {
  return actor.role === 'TENANT_ADMIN'
}

/**
 * Which application a signed-in user lands in. Platform staff have no boards to show, so
 * sending them to the board list would show them an empty page they cannot fill. The
 * system admin and support see only that they are signed in: tenants and feedback are
 * administered at Teamcentric Accounts.
 */
export function homeViewFor(actor: Pick<Actor, 'role'>): 'boards' | 'support' {
  return actor.role === 'SYSTEM_SUPPORT' || actor.role === 'SYSTEM_ADMIN' ? 'support' : 'boards'
}

/**
 * What an integration token may be used for. A token acts as its user, so these narrow
 * that user's access and never widen it: a `boards:write` token still cannot edit a board
 * its user only views.
 *
 * Stored explicitly on each token. A scope added here later is not granted to tokens that
 * already exist.
 */
export const INTEGRATION_SCOPES = ['boards:read', 'boards:write', 'boards:create'] as const
export type IntegrationScope = (typeof INTEGRATION_SCOPES)[number]

export function isIntegrationScope(value: unknown): value is IntegrationScope {
  return typeof value === 'string' && (INTEGRATION_SCOPES as readonly string[]).includes(value)
}

/**
 * Whether granted scopes cover a needed one. `boards:write` implies `boards:read`: a
 * client that may change a board has to be able to read the revision it is changing.
 */
export function hasIntegrationScope(granted: readonly string[], needed: IntegrationScope): boolean {
  if (granted.includes(needed)) return true
  return needed === 'boards:read' && granted.includes('boards:write')
}
